import type { DropdownInputOption } from "types/DropdownInputOption";
import type { Class } from "types/Class";

import { getClasses, getClassesFromAcademicYear } from "./classServices";
import {
  getAllClassesCache,
  setAllClassesCache,
  getAllClassesPromise,
  setAllClassesPromise,
  getYearClassesCache,
  setYearClassesCache,
  getYearClassesPromise,
  setYearClassesPromise,
} from "./classOptionCache";

type ClassOptionSortOrder = "asc" | "desc";

const dedupeClasses = (classes: Class[]): Class[] => {
  const seen = new Set<number>();

  return classes.filter((classItem) => {
    if (seen.has(classItem.id)) return false;

    seen.add(classItem.id);
    return true;
  });
};

const getAllClassesCachedSnapshot = (): Class[] => getAllClassesCache() || [];

const getAllClassesCached = async (): Promise<Class[]> => {
  const cachedClasses = getAllClassesCache();
  if (cachedClasses) return cachedClasses;

  const pendingRequest = getAllClassesPromise();
  if (pendingRequest) return pendingRequest;

  const request = getClasses()
    .then((classes) => {
      setAllClassesCache(classes);
      return classes;
    })
    .finally(() => {
      setAllClassesPromise(null);
    });

  setAllClassesPromise(request);
  return request;
};

const getClassesForAcademicYearCached = async (
  year: number,
): Promise<Class[]> => {
  const cachedClasses = getYearClassesCache(year);
  if (cachedClasses) return cachedClasses;

  const pendingRequest = getYearClassesPromise(year);
  if (pendingRequest) return pendingRequest;

  const request = getClassesFromAcademicYear(year)
    .then((classes) => {
      setYearClassesCache(year, classes);
      return classes;
    })
    .finally(() => {
      setYearClassesPromise(year, null);
    });

  setYearClassesPromise(year, request);
  return request;
};

const getClassesForAcademicYearsCachedSnapshot = (years: number[]): Class[] => {
  const uniqueYears = Array.from(new Set(years));
  const cachedClasses = uniqueYears.flatMap(
    (year) => getYearClassesCache(year) || [],
  );

  return dedupeClasses(cachedClasses);
};

const getClassesForAcademicYearsCached = async (
  years: number[],
): Promise<Class[]> => {
  const uniqueYears = Array.from(new Set(years));
  const classesByYear = await Promise.all(
    uniqueYears.map((year) => getClassesForAcademicYearCached(year)),
  );

  return dedupeClasses(classesByYear.flat());
};

const compareClassName = (leftName: string, rightName: string) =>
  leftName.localeCompare(rightName, "ja");

const buildClassOptions = (
  classes: Class[],
  sortOrder: ClassOptionSortOrder = "desc",
): DropdownInputOption[] => {
  const dedupedClasses = dedupeClasses(classes);

  return dedupedClasses
    .sort((leftClass, rightClass) => {
      if (leftClass.year !== rightClass.year) {
        return sortOrder === "asc"
          ? leftClass.year - rightClass.year
          : rightClass.year - leftClass.year;
      }

      return compareClassName(leftClass.name, rightClass.name);
    })
    .map((classItem) => ({
      label: `${classItem.year} - ${classItem.name}`,
      value: classItem.id.toString(),
    }));
};

const parseClassOption = (option: DropdownInputOption) => {
  const [yearLabel, ...nameParts] = option.label.split(" - ");
  const parsedYear = Number(yearLabel);

  return {
    year: Number.isNaN(parsedYear) ? 0 : parsedYear,
    name: nameParts.join(" - "),
  };
};

const mergeClassOptions = (
  optionGroups: DropdownInputOption[][],
  sortOrder: ClassOptionSortOrder = "desc",
): DropdownInputOption[] => {
  const mergedOptions = optionGroups.flat();
  const uniqueOptions = new Map<string, DropdownInputOption>();

  for (const option of mergedOptions) {
    if (!uniqueOptions.has(option.value)) {
      uniqueOptions.set(option.value, option);
    }
  }

  return Array.from(uniqueOptions.values()).sort((leftOption, rightOption) => {
    const leftClass = parseClassOption(leftOption);
    const rightClass = parseClassOption(rightOption);

    if (leftClass.year !== rightClass.year) {
      return sortOrder === "asc"
        ? leftClass.year - rightClass.year
        : rightClass.year - leftClass.year;
    }

    return compareClassName(leftClass.name, rightClass.name);
  });
};

export {
  getAllClassesCachedSnapshot,
  getAllClassesCached,
  getClassesForAcademicYearsCachedSnapshot,
  getClassesForAcademicYearsCached,
  buildClassOptions,
  mergeClassOptions,
};
